/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { WEB_SIDEBAR_BREAKPOINT } from '../../../shared/layout/breakpoints';

export type HubSegmentChip<K extends string> = {
  key: K;
  label: string;
  testID: string;
};

/**
 * Fila de segmentos compartida por los hubs de navegación (Comisiones, Precios,
 * Catálogo). Mobile: chips horizontales con scroll — estilo ticket gold.
 * Desktop (>= WEB_SIDEBAR_BREAKPOINT): tabs con underline anclados al borde
 * inferior, patrón de navegación de sección.
 */
export function HubSegmentChips<K extends string>({
  segments,
  active,
  onChange,
}: {
  segments: Array<HubSegmentChip<K>>;
  active: K;
  onChange: (key: K) => void;
}) {
  const { width } = useWindowDimensions();
  const isWide = width >= WEB_SIDEBAR_BREAKPOINT;

  return (
    <View style={[styles.chromeWrap, isWide && styles.chromeWrapWide]}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.segmentScroll}
        contentContainerStyle={styles.segmentRow}
        accessibilityRole="tablist"
      >
        {segments.map((segment) => {
          const selected = segment.key === active;
          const segmentStyle = isWide
            ? [styles.tab, selected && styles.tabSelected]
            : [styles.segment, selected && styles.segmentSelected];
          const labelStyle = isWide
            ? [styles.tabLabel, selected && styles.tabLabelSelected]
            : [styles.segmentLabel, selected && styles.segmentLabelSelected];
          return (
            <TouchableOpacity
              key={segment.key}
              style={segmentStyle}
              onPress={() => onChange(segment.key)}
              accessibilityRole="tab"
              accessibilityState={{ selected }}
              accessibilityLabel={segment.label}
              testID={segment.testID}
            >
              <Text style={labelStyle}>{segment.label}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  segmentScroll: { flexGrow: 0 },
  chromeWrap: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xs,
  },
  // Desktop: la fila funciona como tab bar — borde inferior que la ancla al contenido.
  chromeWrapWide: {
    paddingBottom: 0,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  segmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  // Mobile — pill seleccionable.
  segment: {
    flexShrink: 0,
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  segmentSelected: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  segmentLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  segmentLabelSelected: {
    color: ticketColors.goldInk,
    fontWeight: '700',
  },
  // Desktop — tab con underline.
  tab: {
    flexShrink: 0,
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    marginBottom: -StyleSheet.hairlineWidth,
  },
  tabSelected: {
    borderBottomColor: ticketColors.gold,
  },
  tabLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  tabLabelSelected: {
    color: ticketColors.ink,
    fontWeight: '700',
  },
});
